import { Button, Card, Flex, Skeleton, Tooltip } from 'antd';
import { FunctionComponent, ReactNode, useReducer, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';

// components
import StatusComponent from 'components/status/status';

// styles
import styles from './actions.module.scss';
import { ServiceCost } from './commonModels';
import { isRTL } from 'core/services/language';
import Icon from 'utils/icon';
import PlanModalComponent from 'core/layouts/main/components/planModal';
import { RootState } from 'stores/store';
import { useSelector } from 'react-redux';

interface ListHeaderComponentProps {
	title: string;
	subTitle: string;
	costs?: ServiceCost[];
	costLoading?: boolean;
	customNavigate?: () => void;
	addAvailable?: boolean;
	addAvailableText?: string;
	newRequestText?: string;
	addonData?: ReactNode;
}

const ActionListHeaderComponent: FunctionComponent<ListHeaderComponentProps> = ({
	title,
	subTitle,
	costs,
	costLoading,
	addAvailable = true,
	addAvailableText,
	newRequestText,
	addonData,
	customNavigate,
}) => {
	const { t } = useTranslation();
	const navigate = useNavigate();

	const company = useSelector((state: RootState) => state.company);

	const [plansOpen, setPlansOpen] = useState(false);

	return (
		<>
			<Card className="mb-4">
				<Flex justify="space-between" align="center" wrap>
					<div className={styles.headerContainer}>
						<Flex gap={20} align="center" className="mb-2" wrap>
							<div>
								<h5 style={{ marginBottom: 0, minWidth: 200 }}>{title}</h5>
								<p className="text-light mb-0">{subTitle}</p>
							</div>

							<Flex gap={10} vertical={costs && costs.length > 1}>
								{costLoading ? (
									<Skeleton.Button active style={{ width: 100, height: 35, borderRadius: '6px' }} />
								) : (
									costs &&
									costs.length > 0 &&
									costs.map((cost, index) => (
										<Flex gap={20} key={index}>
											<StatusComponent
												bordered
												text={
													<StatusComponent
														text={
															<>
																{cost?.name && <span> {t('ACTIONS.' + cost.name) + ': '}</span>}
																{!cost || cost?.payment_method === 'free' ? (
																	t('ACTIONS.UNLIMITED')
																) : cost?.payment_method === 'usage' ? (
																	t('COMMON.REMAINING_TRANSACTION', {
																		num: cost?.payment_details.usage_required + cost?.payment_details.usage_remaining_after,
																	})
																) : cost?.payment_method === 'points' || cost?.payment_method === null ? (
																	<span style={{ color: '#099137', fontWeight: 500 }}>
																		{t('ACTIONS.COST')}:{' '}
																		<span
																			style={{
																				fontWeight: 500,
																				...(cost.cost_breakdown?.discount
																					? { color: '#ff3535', textDecoration: 'line-through', marginInline: 10 }
																					: {}),
																			}}>
																			{Number(cost?.cost_breakdown?.discount ? cost?.cost_breakdown?.original_cost : cost.payment_details.points_required).toLocaleString('en-US')}{' '}
																			{t('COMMON.POINTS')}
																		</span>
																		<span style={{ fontWeight: 500 }}>
																			{cost.cost_breakdown?.discount && Number(cost.cost_breakdown.cost_after_discount).toLocaleString('en-US') + ' ' + t('COMMON.POINTS')}
																		</span>
																	</span>
																) : (
																	Number(cost?.payment_details.points_required).toLocaleString('en-US') + ' ' + t('COMMON.POINTS')
																)}
															</>
														}
														style={{ padding: '2px 7px', borderRadius: 10 }}
														color={cost?.payment_method === 'points' ? undefined : 'green'}
														small
														iconName={cost?.payment_method === 'points' ? 'wallet' : 'energy'}
														iconHeight={cost?.payment_method === 'points' ? 17 : 15}
														iconWidth={cost?.payment_method === 'points' ? 20 : 10}
													/>
												}
											/>

											{cost?.cost_breakdown?.discount && (
												<StatusComponent
													text={+cost?.cost_breakdown?.discount?.percentage + '% ' + t('ACTIONS.DISCOUNT')}
													color="green"
													style={{ fontWeight: 500 }}
												/>
											)}

											{cost?.upgrade_benefits && cost?.upgrade_benefits.length > 0 && (
												<StatusComponent
													bordered
													text={
														<Flex gap={10}>
															<StatusComponent
																text={`${cost?.upgrade_benefits[0].free_uses} ${t('ACTIONS.FREE_ACTIONS')}`}
																color="green"
																small
																style={{ padding: '1px 5px' }}
																iconName={'energy'}
																iconHeight={15}
																iconWidth={10}
															/>

															<Button type="link" size="middle" style={{ padding: 0, height: 'auto' }} onClick={() => setPlansOpen(true)}>
																<Flex gap={5} align="center">
																	{t('ACTIONS.UPGRADE_TO_PLAN', { name: cost?.upgrade_benefits[0].plan_name })}
																	<Icon name="info2" color="var(--primary)" height={12} width={12} />
																</Flex>
															</Button>
														</Flex>
													}
													style={{ background: '#fafafa', border: '1px solid #eee', paddingBlock: 4 }}
												/>
											)}
										</Flex>
									))
								)}

								{addonData}
							</Flex>
						</Flex>
					</div>

					{costLoading ? (
						<Skeleton.Button active style={{ width: 98, height: 32, borderRadius: 'var(--radius)' }} />
					) : addAvailable ? (
						costs && !costs.some((cost) => cost?.can_access) ? (
							<Button type="primary" size="middle" onClick={() => navigate('/dashboard/wallet')}>
								{t('COMMON.CHARGE')}
							</Button>
						) : (
							<Tooltip color='red' title={company?.profile_status === 'GeneralSuspended' ? t('COMMON.COMPANY_GENERAL_SUSPENDED') : ''}>
								<Button
									type="primary"
									size="middle"
									disabled={(costs && !costs.some((cost) => cost?.can_access)) || company?.profile_status === 'GeneralSuspended'}
									onClick={customNavigate ? customNavigate : () => navigate('create')}>
									{newRequestText ?? t('ACTIONS.NEW_REQUEST')}
								</Button>
							</Tooltip>
						)
					) : (
						addAvailableText && <StatusComponent text={addAvailableText} color="red" />
					)}
				</Flex>
			</Card>

			<PlanModalComponent open={plansOpen} onClose={() => setPlansOpen(false)} currentPlanKey={company?.active_subscription?.key} />
		</>
	);
};

export default ActionListHeaderComponent;
